import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Checkbox } from '~/components/ui/checkbox'; // Assuming you have a Checkbox component (e.g., from shadcn/ui)
import { Label } from '~/components/ui/label';     // Assuming you have a Label component
import { Button } from '~/components/ui/button';   // Assuming you have a Button component
import { toast } from 'sonner'; // Or your preferred notification library
import { trpc } from '~/api/trpc';
import { useUserStore } from '~/stores/user-store';
import { useShallow } from 'zustand/react/shallow';
import { UserRole } from '~/types/server-types/UserRole';

type PractitionerAssignmentData = {
    id: number;
    firstName: string;
    lastName: string;
    title: string;
    categoryId: number | null;
    categoryName: string | null;
    isAssigned: boolean;
};

type TreatmentVariantPractitionersProps = {
    variantId: string;
};

const UNCATEGORIZED_LABEL = 'Ohne Kategorie';

const TreatmentVariantPractitioners = ({ variantId }: TreatmentVariantPractitionersProps) => {
    const { userRole } = useUserStore(
        useShallow((state) => ({
            userRole: state.user?.role
        }))
    );
    const shouldDisableAllInputs = userRole !== UserRole.ADMIN;

    const utils = trpc.useUtils(); // Use useUtils() if on tRPC v10+, otherwise useContext() for v9

    // --- State ---
    // Stores the state of checkboxes managed by the user in the UI
    const [checkedState, setCheckedState] = useState<Map<number, boolean>>(new Map());
    // Stores the initial fetched assignment state to compare for changes
    const [initialAssignments, setInitialAssignments] = useState<Map<number, boolean>>(new Map());
    const [hasChanges, setHasChanges] = useState(false);

    // --- tRPC Queries and Mutations ---
    const { data: practitioners, isLoading, error, refetch } = trpc.practitioners.getForVariantAssignment.useQuery(
        { variantId },
        {
            refetchOnWindowFocus: false,
        }
    );

    const updateAssociationsMutation = trpc.practitioners.updateVariantAssociations.useMutation({
        onSuccess: async () => {
            toast.success('Practitioner assignments updated successfully!');
            // Invalidate the query to refetch fresh data reflecting the save
            await utils.practitioners.getForVariantAssignment.invalidate({ variantId });
            // Optionally, you could also invalidate related queries if needed
            // e.g., utils.treatmentVariants.getOne.invalidate({ id: variantId });
            setHasChanges(false); // Reset change tracking
        },
        onError: (error) => {
            console.error("Failed to update assignments:", error);
            toast.error('Failed to update assignments. Please try again.');
        },
    });

    // --- Effects ---
    // Initialize checkbox state when data loads or changes
    useEffect(() => {
        if (practitioners) {
            const initialChecked = new Map<number, boolean>();
            practitioners.forEach(p => {
                initialChecked.set(p.id, p.isAssigned);
            });
            setCheckedState(new Map(initialChecked)); // Use a copy for local state
            setInitialAssignments(initialChecked); // Store the initial state
            setHasChanges(false); // Reset changes when data reloads
        }
    }, [practitioners]);

    // Check if current state differs from initial state
    useEffect(() => {
        if (!practitioners) {
            setHasChanges(false);
            return;
        }
        let changed = false;
        for (const [id, isChecked] of checkedState.entries()) {
            if (initialAssignments.get(id) !== isChecked) {
                changed = true;
                break;
            }
        }
        setHasChanges(changed);
    }, [checkedState, initialAssignments, practitioners]);


    // --- Grouping ---
    // Group practitioners by their category and sort groups by category id
    // (so e.g. doctors come first, cosmeticians second). Practitioners without
    // a category are grouped last.
    const groupedPractitioners = useMemo(() => {
        if (!practitioners) return [];

        const groups = new Map<number | null, { categoryId: number | null; categoryName: string; practitioners: PractitionerAssignmentData[] }>();

        for (const practitioner of practitioners) {
            const key = practitioner.categoryId;
            if (!groups.has(key)) {
                groups.set(key, {
                    categoryId: key,
                    categoryName: practitioner.categoryName ?? UNCATEGORIZED_LABEL,
                    practitioners: [],
                });
            }
            groups.get(key)!.practitioners.push(practitioner);
        }

        return Array.from(groups.values()).sort((a, b) => {
            // Uncategorized practitioners go last
            if (a.categoryId === null) return 1;
            if (b.categoryId === null) return -1;
            return a.categoryId - b.categoryId;
        });
    }, [practitioners]);

    // --- Handlers ---
    const handleCheckboxChange = useCallback((practitionerId: number, isChecked: boolean) => {
        setCheckedState(prev => {
            const newState = new Map(prev);
            newState.set(practitionerId, isChecked);
            return newState;
        });
    }, []);

    const handleSaveChanges = useCallback(() => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You do not have permission to do this.");
            return;
        }
        if (!hasChanges || updateAssociationsMutation.isPending) {
            return; // Don't save if no changes or already saving
        }

        const assignedPractitionerIds = Array.from(checkedState.entries())
            .filter(([_, isChecked]) => isChecked)
            .map(([id]) => id);

        updateAssociationsMutation.mutate({
            variantId,
            assignedPractitionerIds,
        });
    }, [checkedState, variantId, updateAssociationsMutation, hasChanges]);

    // --- Render Logic ---
    if (isLoading) {
        return <div>Loading practitioners...</div>;
    }

    if (error) {
        return <div className="text-red-600">Error loading practitioners: {error.message}</div>;
    }

    if (!practitioners) {
        return <div>No practitioner data found.</div>; // Should ideally not happen if no error
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-medium">Assignable Practitioners</h3>
            {practitioners.length === 0 ? (
                 <p className="text-sm text-gray-500">No active practitioners found.</p>
            ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto border p-4 rounded-md"> {/* Add scroll if needed */}
                    {groupedPractitioners.map((group) => (
                        <div key={group.categoryId ?? 'uncategorized'} className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">
                                {group.categoryName}
                            </h4>
                            {group.practitioners.map((practitioner) => (
                                <div key={practitioner.id} className="flex items-center space-x-2 pl-1">
                                    <Checkbox
                                        id={`practitioner-${practitioner.id}`}
                                        checked={checkedState.get(practitioner.id) ?? false}
                                        onCheckedChange={(checked) => handleCheckboxChange(practitioner.id, !!checked)} // !!checked converts CheckedState to boolean
                                        disabled={updateAssociationsMutation.isPending || shouldDisableAllInputs}
                                    />
                                    <Label htmlFor={`practitioner-${practitioner.id}`} className="cursor-pointer">
                                        {practitioner.firstName} {practitioner.lastName}
                                        {practitioner.title && <span className="text-sm text-gray-500 ml-1">({practitioner.title})</span>}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            <div className="flex justify-end space-x-2">
                 <Button
                    variant="outline"
                    onClick={() => refetch()} // Simple way to reset changes is to refetch
                    disabled={updateAssociationsMutation.isPending || shouldDisableAllInputs}
                 >
                    Discard Changes
                 </Button>
                 <Button
                    onClick={handleSaveChanges}
                    disabled={!hasChanges || updateAssociationsMutation.isPending || shouldDisableAllInputs}
                 >
                    {updateAssociationsMutation.isPending ? 'Saving...' : 'Save Changes'}
                 </Button>
            </div>
        </div>
    );
};

export default TreatmentVariantPractitioners;